import { useState, useCallback } from 'react';

/**
 * Custom hook for managing modal states in a consolidated way
 * This reduces the number of useState calls and provides a cleaner API
 */
export const useModalState = () => {
    const [modals, setModals] = useState({
        unlock: false,
        paywall: false,
        cliffhangerPaywall: false,
        trialExpired: false,
        storybookUpsell: false,
        streamlinedUnmute: false,
        welcome: false,
        switchProfile: false,
        profilePicker: false,
    });

    const [modalContexts, setModalContexts] = useState({
        paywall: null,
        cliffhangerPaywall: null,
        switchProfile: null,
    });

    const [modalFlags, setModalFlags] = useState({
        hasShownWelcomeThisSession: false,
        storyCompletion: false,
    });

    // Generic modal control functions
    const openModal = useCallback((modalName, context = null) => {
        setModals(prev => ({ ...prev, [modalName]: true }));
        if (context && modalContexts[modalName] !== undefined) {
            setModalContexts(prev => ({ ...prev, [modalName]: context }));
        }
    }, [modalContexts]);

    const closeModal = useCallback((modalName) => {
        setModals(prev => ({ ...prev, [modalName]: false }));
        if (modalContexts[modalName] !== undefined) {
            setModalContexts(prev => ({ ...prev, [modalName]: null }));
        }
    }, [modalContexts]);

    const toggleModal = useCallback((modalName, context = null) => {
        setModals(prev => {
            const isOpen = prev[modalName];
            return { ...prev, [modalName]: !isOpen };
        });
        if (context && modalContexts[modalName] !== undefined) {
            setModalContexts(prev => ({ ...prev, [modalName]: context }));
        }
    }, [modalContexts]);

    // Specific modal control functions for backward compatibility
    const setUnlockModalOpen = useCallback((isOpen) => {
        setModals(prev => ({ ...prev, unlock: isOpen }));
    }, []);

    const setPaywallOpen = useCallback((isOpen, context = null) => {
        setModals(prev => ({ ...prev, paywall: isOpen }));
        if (context) {
            setModalContexts(prev => ({ ...prev, paywall: context }));
        }
    }, []);

    const setCliffhangerPaywallOpen = useCallback((isOpen, context = null) => {
        setModals(prev => ({ ...prev, cliffhangerPaywall: isOpen }));
        if (context) {
            setModalContexts(prev => ({ ...prev, cliffhangerPaywall: context }));
        }
    }, []);

    const setTrialExpiredModalOpen = useCallback((isOpen) => {
        setModals(prev => ({ ...prev, trialExpired: isOpen }));
    }, []);

    const setStorybookUpsellOpen = useCallback((isOpen) => {
        setModals(prev => ({ ...prev, storybookUpsell: isOpen }));
    }, []);

    const setStreamlinedUnmuteOpen = useCallback((isOpen) => {
        setModals(prev => ({ ...prev, streamlinedUnmute: isOpen }));
    }, []);

    const setShowWelcomeModal = useCallback((isOpen) => {
        setModals(prev => ({ ...prev, welcome: isOpen }));
    }, []);

    const setSwitchProfileModalOpen = useCallback((isOpen, profile = null) => {
        setModals(prev => ({ ...prev, switchProfile: isOpen }));
        if (profile) {
            setModalContexts(prev => ({ ...prev, switchProfile: profile }));
        } else if (!isOpen) {
            // Clear the profile context when closing the modal
            setModalContexts(prev => ({ ...prev, switchProfile: null }));
        }
    }, []);

    const setProfilePickerModalOpen = useCallback((isOpen) => {
        setModals(prev => ({ ...prev, profilePicker: isOpen }));
    }, []);

    // Flag control functions
    const setHasShownWelcomeModalThisSession = useCallback((hasShown) => {
        setModalFlags(prev => ({ ...prev, hasShownWelcomeThisSession: hasShown }));
    }, []);

    const setStoryCompletionFlag = useCallback((flag) => {
        setModalFlags(prev => ({ ...prev, storyCompletion: flag }));
    }, []);

    return {
        // Modal states
        isUnlockModalOpen: modals.unlock,
        isPaywallOpen: modals.paywall,
        isTrialExpiredModalOpen: modals.trialExpired,
        isStorybookUpsellOpen: modals.storybookUpsell,
        isStreamlinedUnmuteOpen: modals.streamlinedUnmute,
        showWelcomeModal: modals.welcome,
        isSwitchProfileModalOpen: modals.switchProfile,
        isProfilePickerModalOpen: modals.profilePicker,
        paywallContext: modalContexts.paywall,
        profileToSwitchTo: modalContexts.switchProfile,

        // Modal flags
        hasShownWelcomeModalThisSession: modalFlags.hasShownWelcomeThisSession,
        storyCompletionFlag: modalFlags.storyCompletion,

        // Modal control functions
        openModal,
        closeModal,
        toggleModal,
        setUnlockModalOpen,
        setPaywallOpen,
        setCliffhangerPaywallOpen,
        setTrialExpiredModalOpen,
        setStorybookUpsellOpen,
        setStreamlinedUnmuteOpen,
        setShowWelcomeModal,
        setSwitchProfileModalOpen,
        setProfilePickerModalOpen,
        setHasShownWelcomeModalThisSession,
        setStoryCompletionFlag,
    };
};

